import { useApproval, useUser } from '@trustpad/launchpad';
import { useStakingProvider } from '@trustpad/launchpad/dist/staking/hooks/useStaking';

import * as React from 'react';
import { useState } from 'react';
import { FaLock } from 'react-icons/fa';

import { CustomInput } from '~common/CustomInput';
import { CardTitle, MaxButton } from '~common/UI';
import { useModalProvider } from '~common/hooks/useModalProvider';
import { APPROVE_VALUE, ZERO_BN } from '~config/constants';
import { ActionButton } from '~features/ui';
import { fNum, isValidValue, normalize, toEth, toWeiBN } from '~utils';

export function DepositCard({
  symbol,
  lockDeposit,
}: {
  symbol: string;
  lockDeposit: boolean;
}) {
  const [depositValue, setDepositValue] = React.useState<string>('');
  const { account } = useUser();
  const { pairContract, deposit, userStats, pool } = useStakingProvider();
  const amountBN = isValidValue(depositValue) ? toWeiBN(depositValue) : ZERO_BN;
  const {
    isApproved,
    isLoading: isApproving,
    approveSpending,
  } = useApproval({
    tokenContract: pairContract,
    spenderAddress: pool.address,
    amount: amountBN,
  });
  const { setMessage } = useModalProvider();

  const [isLoading, setIsLoading] = useState(false);

  const onApprove = () => approveSpending(APPROVE_VALUE).then(setMessage);

  async function onClick() {
    setIsLoading(true);
    await deposit(depositValue).then(setMessage);
    setIsLoading(false);
    setDepositValue('');
  }

  const isMoreThanBalance = amountBN.gt(userStats.lpBalance);

  return (
    <div>
      <CardTitle>Deposit</CardTitle>

      <div className="max-w-sm mx-auto">
        <CustomInput
          name="deposit"
          value={depositValue}
          onChange={(v: string) => setDepositValue(normalize(v))}
          topLabel={'Deposit'}
          topRight={`Your balance: ${fNum(userStats.lpBalance)} ${symbol}`}
          hasError={isMoreThanBalance}
          button={
            <MaxButton
              onClick={() => setDepositValue(toEth(userStats.lpBalance))}
            />
          }
        />
      </div>

      <div className="text-right mt-3 ml-auto">
        {isApproved ? (
          <ActionButton
            isLoading={isLoading}
            isDisabled={
              lockDeposit || isLoading || !amountBN.gtn(0) || isMoreThanBalance
            }
            onClick={onClick}>
            Deposit {lockDeposit && <FaLock className="inline" />}
          </ActionButton>
        ) : (
          <ActionButton
            isLoading={isApproving}
            isDisabled={lockDeposit || !account || isApproving}
            onClick={onApprove}>
            Approve {lockDeposit && <FaLock className="inline" />}
          </ActionButton>
        )}
      </div>
    </div>
  );
}
